'use client';

/**
 * EntityPreviewDrawer — the SHELL-LEVEL in-dashboard entity profile (polish-backlog
 * bet F, second half).
 *
 * History: the original EntityPreviewDrawer (Slice-1 Task 5) was retired on the
 * /entities page when the graph + directory got ONE unified inspector (Lam,
 * 2026-06-09: "one preview, not two"). THIS file brings the drawer back one level
 * UP — mounted once in <DashboardShell>, so every OTHER surface in the chain hub
 * (⌘K palette, Overview token/social/pulse rows, the Social page's no-handle
 * entities) can open a rich in-shell profile instead of bouncing the user out to
 * /tags/[slug]. The /entities page keeps its unified inspector and does NOT use
 * this drawer.
 *
 * URL POLICY (Lam, 2026-06-10 — docs/superpowers/plans/2026-06-10-tags-migration-
 * panel-synthesis.md): `/tags/[slug]` is the canonical entity URL FOR NOW; the
 * `/entities/[slug]` rename is decided but ships as its own discrete post-push
 * event with the domain cutover. This drawer is a VIEW, not a route — it never
 * changes the URL, and it always renders a "View full profile →" link to the
 * canonical page. No chain-scoped entity URLs.
 *
 * CONTENT (Lam, 2026-06-10: two containers, ONE content component): the drawer
 * renders the SAME <EntityProfileContent> core the graph inspector wraps —
 * header, RECON read, TYPED RELATIONSHIP labels + narratives, connections,
 * links, lazy recent coverage, canonical footer. The two previews can't drift.
 *
 * Data: self-fetches ONE payload on open from `/api/entity-profile/[slug]`
 * (public GET; Admin-SDK reads server-side so the CONFIRMED typed edges — which
 * a client can't query with labels — ride along with the tag projection, RECON
 * intel and the related rail). Nothing is fetched until a profile is opened.
 *
 * Exports:
 *   • EntityProfileProvider — mounted ONCE in DashboardShell; owns the open slug
 *     and renders the drawer.
 *   • useEntityProfileOpener — `((slug) => void) | null`; null outside the shell.
 *   • EntityProfileLink — a crawlable `<a href="/tags/[slug]">` that intercepts a
 *     PLAIN left-click to open the drawer (same recipe as the Directory cards);
 *     outside the provider (marketing surfaces) it degrades to a normal link.
 *     Modified clicks (cmd/ctrl/shift/middle) always navigate.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import Link from 'next/link';

import type { ChainMeta } from '@/lib/chains';
import { Eyebrow, Skeleton } from './atoms';
import EntityProfileContent, {
  type ProfileEntityRef,
  type ProfileLinkChip,
  type ProfileRecon,
  type ProfileRelationship,
} from './EntityProfileContent';
import RecapShareButton from './RecapShareButton';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://descout.xyz';

/* ─────────────────────────────────────────────────────────────────────────────
   Context — the opener every in-shell surface reads.
───────────────────────────────────────────────────────────────────────────── */

const EntityProfileContext = createContext<((slug: string) => void) | null>(null);

/**
 * The in-shell profile opener, or null when rendered OUTSIDE the dashboard shell
 * (marketing surfaces reuse some of the same row components — they fall back to
 * plain `/tags/[slug]` navigation).
 */
export function useEntityProfileOpener(): ((slug: string) => void) | null {
  return useContext(EntityProfileContext);
}

/* ─────────────────────────────────────────────────────────────────────────────
   EntityProfileLink — crawlable canonical link, in-shell click → drawer.
───────────────────────────────────────────────────────────────────────────── */

export function EntityProfileLink({
  slug,
  className,
  style,
  children,
}: {
  slug: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const open = useEntityProfileOpener();

  const onClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    // No opener (outside the shell) → behave as a normal link. Respect modified
    // clicks (open-in-new-tab etc.) — only intercept a plain left-click.
    if (
      !open ||
      ev.defaultPrevented ||
      ev.button !== 0 ||
      ev.metaKey ||
      ev.ctrlKey ||
      ev.shiftKey ||
      ev.altKey
    ) {
      return;
    }
    ev.preventDefault();
    open(slug);
  };

  // A real <a> (SSR-crawlable, middle-clickable) — NOT a router push.
  return (
    <a href={`/tags/${slug}`} onClick={onClick} className={className} style={style}>
      {children}
    </a>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Provider — owns the open slug; mounted once in DashboardShell.
───────────────────────────────────────────────────────────────────────────── */

export function EntityProfileProvider({
  chain,
  children,
}: {
  chain: ChainMeta;
  children: ReactNode;
}) {
  const [slug, setSlug] = useState<string | null>(null);
  const open = useCallback((s: string) => setSlug(s), []);
  const close = useCallback(() => setSlug(null), []);

  return (
    <EntityProfileContext.Provider value={open}>
      {children}
      <EntityProfileDrawer slug={slug} chain={chain} onClose={close} onSelect={open} />
    </EntityProfileContext.Provider>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Profile payload — the `/api/entity-profile/[slug]` response shape (the route
   mirrors EntityProfileContent's props contract; keep the two in sync).
───────────────────────────────────────────────────────────────────────────── */

interface EntityProfileResponse {
  entity: {
    id: string;
    slug: string;
    name: string;
    type: string;
    handle?: string;
    /** True when we ACTIVELY poll this handle (live dot next to @handle). */
    tracked: boolean;
    image?: string;
    /** Chain-membership facet (drives the chain chip on the active chain). */
    chains: string[];
    /** Markdown-stripped ground-truth excerpt (glossaryContent ?? seoExcerpt ?? description). */
    blurb?: string;
  };
  recon: ProfileRecon | null;
  /** Linked identity asset, when any. */
  asset: { kind: 'token' | 'collection'; assetId: string } | null;
  links: ProfileLinkChip[];
  /** CONFIRMED typed edges, perspectivized — Lam's headline ask: the drawer
      shows the SAME relationship labels the graph inspector does. */
  relationships: ProfileRelationship[];
  /** Generic associations (typed counterparts pre-deduped server-side). */
  related: ProfileEntityRef[];
}

/* ─────────────────────────────────────────────────────────────────────────────
   Drawer — right sheet (desktop) / bottom sheet (mobile).
───────────────────────────────────────────────────────────────────────────── */

type FetchState =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'ready'; data: EntityProfileResponse };

function EntityProfileDrawer({
  slug,
  chain,
  onClose,
  onSelect,
}: {
  slug: string | null;
  chain: ChainMeta;
  onClose: () => void;
  /** Re-open a related entity in place. */
  onSelect: (slug: string) => void;
}) {
  const open = !!slug;
  const [state, setState] = useState<FetchState>({ status: 'loading' });

  // ONE fetch per open slug — tag projection + typed edges + RECON + related.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      try {
        const res = await fetch(`/api/entity-profile/${encodeURIComponent(slug)}`);
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'missing' });
          return;
        }
        const data = (await res.json()) as EntityProfileResponse;
        if (!cancelled) {
          if (!data?.entity?.slug) {
            setState({ status: 'missing' });
          } else {
            setState({ status: 'ready', data });
          }
        }
      } catch {
        if (!cancelled) setState({ status: 'missing' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Esc to dismiss + lock body scroll while open. Listeners cleaned up on close/unmount.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !slug) return null;

  return (
    // z-[100] sits ABOVE the shell's MobileNav (z-60) + TopBar (z-50).
    <div
      className="fixed inset-0 z-[100]"
      role="dialog"
      aria-modal="true"
      aria-label="Entity profile"
    >
      {/* Backdrop — click outside to dismiss. */}
      <button
        type="button"
        aria-label="Close profile"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-ink/60 backdrop-blur-[1px]"
      />

      {/* Panel — right sheet on desktop, bottom sheet on mobile. flex-col so the
          shared content's header/chip/footer pin and ONLY its body scrolls. */}
      <div className="absolute inset-x-0 bottom-0 flex max-h-[88vh] flex-col rounded-t-lg border-[0.5px] border-divider bg-surface sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none sm:w-[440px] sm:max-w-[92vw] sm:rounded-none sm:border-l">
        {state.status === 'loading' && <DrawerLoading onClose={onClose} />}

        {state.status === 'missing' && (
          <div className="flex flex-1 flex-col items-start gap-3 p-6">
            <div className="flex w-full items-center justify-between">
              <Eyebrow color="var(--slate)">Profile</Eyebrow>
              <CloseButton onClose={onClose} />
            </div>
            <p className="font-display text-sm text-parchment-dim">
              This profile couldn&rsquo;t be loaded in place. Open the canonical page to
              view it.
            </p>
            <Link
              href={`/tags/${slug}`}
              className="font-mono text-[11px] uppercase tracking-[0.1em] text-scout-gold hover:opacity-80"
            >
              View full profile →
            </Link>
          </div>
        )}

        {state.status === 'ready' && (
          <DrawerBody data={state.data} chain={chain} onClose={onClose} onSelect={onSelect} />
        )}
      </div>
    </div>
  );
}

function DrawerLoading({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-1 flex-col p-5">
      <div className="flex items-start justify-between">
        <div className="flex gap-3.5">
          <Skeleton className="h-[44px] w-[44px] rounded" />
          <div className="flex flex-col gap-2 pt-1">
            <Skeleton className="h-3 w-20 rounded" />
            <Skeleton className="h-5 w-40 rounded" />
          </div>
        </div>
        <CloseButton onClose={onClose} />
      </div>
      <Skeleton className="mt-6 h-3 w-full rounded" />
      <Skeleton className="mt-2 h-3 w-5/6 rounded" />
      <Skeleton className="mt-2 h-3 w-2/3 rounded" />
    </div>
  );
}

/**
 * The drawer's body = the SHARED EntityProfileContent core, plus the drawer's
 * own extras in its slots: chain chip, priceless identity-asset chip, share +
 * close buttons. Related rail + recent coverage + the canonical footer are part
 * of the shared core itself.
 */
function DrawerBody({
  data,
  chain,
  onClose,
  onSelect,
}: {
  data: EntityProfileResponse;
  chain: ChainMeta;
  onClose: () => void;
  onSelect: (slug: string) => void;
}) {
  const { entity } = data;

  // Identity-asset chip (name only — live pricing lives on /market).
  const assetChip = data.asset
    ? {
        kind: data.asset.kind,
        label:
          data.asset.kind === 'token' ? `$${data.asset.assetId.toUpperCase()}` : data.asset.assetId,
        marketHref: `/market?tab=${data.asset.kind === 'token' ? 'tokens' : 'nfts'}&asset=${data.asset.assetId.toLowerCase()}`,
        onNavigate: onClose,
      }
    : null;

  return (
    <EntityProfileContent
      entity={entity}
      brief={entity.blurb}
      recon={data.recon}
      assetChip={assetChip}
      relationships={data.relationships}
      connections={data.related}
      links={data.links}
      chainChip={
        entity.chains.includes(chain.id) ? { color: chain.color, label: chain.label } : null
      }
      onSelect={onSelect}
      headerRight={
        <div className="flex flex-shrink-0 items-start gap-1.5">
          <RecapShareButton title={entity.name} url={`${SITE_URL}/tags/${entity.slug}`} />
          <CloseButton onClose={onClose} />
        </div>
      }
    />
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close profile"
      className="cursor-pointer self-start p-1 text-xl leading-none text-parchment-dim transition-colors hover:text-parchment"
    >
      ×
    </button>
  );
}
